import { BRAND_DISPLAY_NAME } from '@streamlain/brand';

import {
  APP_OPERATOR_HUMANIZED_TIMING_MS,
  type AppOperatorBridgeTarget,
  appOperatorMotionDurationMs,
  type AppOperatorPresentationMode,
  type AppOperatorRect,
  type AppOperatorScrollDirection,
  centerOfAppOperatorRect,
  createNaturalAppOperatorPath,
} from './operator-contract';

const PRESENTATION_MODE_KEY = 'streamlain:app-operator:presentation-mode:v1';

export interface AIOperatorPresenceSnapshot {
  mode: AppOperatorPresentationMode;
  visible: boolean;
  cursorVisible: boolean;
  clicking: boolean;
  cursor: { x: number; y: number } | null;
  fieldRect: AppOperatorRect | null;
  scrollDirection: AppOperatorScrollDirection | null;
  label: string;
}

type Listener = () => void;

function readStoredMode(): AppOperatorPresentationMode {
  try {
    return localStorage.getItem(PRESENTATION_MODE_KEY) === 'instant'
      ? 'instant'
      : 'humanized';
  } catch {
    return 'humanized';
  }
}

function writeStoredMode(mode: AppOperatorPresentationMode): void {
  try {
    localStorage.setItem(PRESENTATION_MODE_KEY, mode);
  } catch {
    // Local preference only; storage failures must not block app operation.
  }
}

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true
  );
}

export class AIOperatorPresentationStore {
  private readonly listeners = new Set<Listener>();
  private readonly pendingDelayCancels = new Set<() => void>();
  private animationToken = 0;
  private snapshot: AIOperatorPresenceSnapshot = {
    mode: readStoredMode(),
    visible: false,
    cursorVisible: false,
    clicking: false,
    cursor: null,
    fieldRect: null,
    scrollDirection: null,
    label: `${BRAND_DISPLAY_NAME} AI`,
  };

  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): AIOperatorPresenceSnapshot => this.snapshot;

  setMode(mode: AppOperatorPresentationMode): void {
    this.advanceAnimationToken();
    writeStoredMode(mode);
    this.patch({
      mode,
      ...(mode === 'instant'
        ? {
            visible: false,
            cursorVisible: false,
            clicking: false,
            fieldRect: null,
            scrollDirection: null,
          }
        : {}),
    });
  }

  async presentTarget(
    target: AppOperatorBridgeTarget | undefined
  ): Promise<void> {
    if (this.isInstantMode() || prefersReducedMotion()) {
      return;
    }
    const rect = target?.fieldRect ?? target?.rect;
    if (!rect) {
      const token = this.advanceAnimationToken();
      this.patch({
        visible: true,
        cursorVisible: true,
        fieldRect: null,
        scrollDirection: target?.scrollDirection ?? null,
      });
      await this.sleep(
        APP_OPERATOR_HUMANIZED_TIMING_MS.targetWithoutGeometry,
        token
      );
      return;
    }

    const destination = centerOfAppOperatorRect(rect);
    const source = this.snapshot.cursor ?? {
      x: destination.x - Math.min(120, rect.width + 48),
      y: destination.y - 32,
    };
    const token = await this.animateCursor(source, destination);
    if (token === null || !this.isTokenActive(token)) return;
    if (target?.scrollDirection) {
      this.patch({ scrollDirection: target.scrollDirection });
      await this.sleep(APP_OPERATOR_HUMANIZED_TIMING_MS.scroll, token);
      if (!this.isTokenActive(token)) return;
    }
    if (target?.fieldRect) {
      this.patch({
        visible: true,
        cursorVisible: false,
        fieldRect: target.fieldRect,
        scrollDirection: null,
      });
      return;
    }
    this.patch({
      visible: true,
      cursorVisible: true,
      clicking: true,
      fieldRect: null,
      scrollDirection: null,
    });
    await this.sleep(APP_OPERATOR_HUMANIZED_TIMING_MS.click, token);
    if (!this.isTokenActive(token)) return;
    this.patch({ clicking: false });
  }

  async presentExecutionResult(
    target: AppOperatorBridgeTarget | undefined
  ): Promise<void> {
    if (this.isInstantMode() || prefersReducedMotion()) return;
    const token = this.animationToken;
    if (target?.fieldRect) {
      this.patch({
        visible: true,
        cursorVisible: false,
        fieldRect: target.fieldRect,
        scrollDirection: null,
      });
      await this.sleep(APP_OPERATOR_HUMANIZED_TIMING_MS.fieldResult, token);
      return;
    }
    await this.sleep(APP_OPERATOR_HUMANIZED_TIMING_MS.actionResult, token);
  }

  clear(): void {
    this.advanceAnimationToken();
    this.patch({
      visible: false,
      cursorVisible: false,
      clicking: false,
      fieldRect: null,
      scrollDirection: null,
    });
  }

  private async animateCursor(
    from: { x: number; y: number },
    to: { x: number; y: number }
  ): Promise<number | null> {
    const token = this.advanceAnimationToken();
    const duration = appOperatorMotionDurationMs({ from, to });
    this.patch({
      visible: true,
      cursorVisible: true,
      fieldRect: null,
      scrollDirection: null,
      cursor: from,
    });
    const completed = await new Promise<boolean>(resolve => {
      const startedAt = performance.now();
      const tick = (now: number) => {
        if (token !== this.animationToken || this.snapshot.mode === 'instant') {
          resolve(false);
          return;
        }
        const progress = Math.min(1, (now - startedAt) / duration);
        this.patch({
          cursor: createNaturalAppOperatorPath({ from, to, progress }),
        });
        if (progress < 1) {
          requestAnimationFrame(tick);
          return;
        }
        resolve(true);
      };
      requestAnimationFrame(tick);
    });
    return completed ? token : null;
  }

  private sleep(ms: number, token: number): Promise<void> {
    if (!this.isTokenActive(token)) {
      return Promise.resolve();
    }
    return new Promise(resolve => {
      let settled = false;
      let timeoutId: number | undefined;
      const finish = () => {
        if (settled) return;
        settled = true;
        if (timeoutId !== undefined) {
          window.clearTimeout(timeoutId);
        }
        this.pendingDelayCancels.delete(finish);
        resolve();
      };
      this.pendingDelayCancels.add(finish);
      timeoutId = window.setTimeout(finish, ms);
    });
  }

  private patch(patch: Partial<AIOperatorPresenceSnapshot>): void {
    this.snapshot = { ...this.snapshot, ...patch };
    for (const listener of this.listeners) listener();
  }

  private isInstantMode(): boolean {
    return this.snapshot.mode === 'instant';
  }

  private isTokenActive(token: number): boolean {
    return token === this.animationToken && !this.isInstantMode();
  }

  private advanceAnimationToken(): number {
    this.animationToken++;
    const cancels = Array.from(this.pendingDelayCancels);
    this.pendingDelayCancels.clear();
    for (const cancel of cancels) cancel();
    return this.animationToken;
  }
}

const stores = new Map<string, AIOperatorPresentationStore>();

export function getAIOperatorPresentationStore(
  customAppId: string
): AIOperatorPresentationStore {
  let store = stores.get(customAppId);
  if (!store) {
    store = new AIOperatorPresentationStore();
    stores.set(customAppId, store);
  }
  return store;
}
